import React from "react";
import DualSided from "svg_icons/planner-extended-data-prep/dual_sided.svg?react";
import DualSidedMidStreetCrossings from "svg_icons/planner-extended-data-prep/dual_sided_mid_street_crossings.svg?react";
import SingleSided from "svg_icons/planner-extended-data-prep/single_sided.svg?react";
import SingleSidedMinimizeDropCrossings from "svg_icons/planner-extended-data-prep/single_sided_minimize_drop_crossings.svg?react";

import { DataPrepConfiguration } from "fond/types";

/**
 * The summary, description and illustration of a cable preference.
 */
export interface CableOptionVariant {
  summary: string;
  description: string;
  icon: React.ReactNode;
}

export interface CableOption {
  preference: DataPrepConfiguration["CablePreference"];
  label: string;
  subOption: { key: "MinimizeDropCrossings" | "IncludeMidStreetCrossings"; label: string };
  // Content for the option when its sub-option is unchecked or checked.
  variants: { unchecked: CableOptionVariant; checked: CableOptionVariant };
}

export const CABLE_OPTIONS: CableOption[] = [
  {
    preference: "single_sided",
    label: "Single-sided cables",
    subOption: { key: "MinimizeDropCrossings", label: "Minimize drop crossings" },
    variants: {
      unchecked: {
        summary: "Single-sided",
        description:
          "Cabling preferred on one side of the road. Drop cables will take a direct route to premises; this may result in many drop crossings.",
        icon: <SingleSided />,
      },
      checked: {
        summary: "Single-sided with limited drop crossings",
        description: "Cabling preferred on one side of the road. Drop cable crossings will be limited to one or two per hub.",
        icon: <SingleSidedMinimizeDropCrossings />,
      },
    },
  },
  {
    preference: "dual_sided",
    label: "Dual-sided cables",
    subOption: { key: "IncludeMidStreetCrossings", label: "Include mid-street crossings" },
    variants: {
      unchecked: {
        summary: "Dual-sided",
        description: "Cabling preferred on both sides of the road. Cables may only cross at intersections.",
        icon: <DualSided />,
      },
      checked: {
        summary: "Dual-sided with mid-street crossings",
        description: "Cabling preferred on both sides of the road. Cables may cross mid-street.",
        icon: <DualSidedMidStreetCrossings />,
      },
    },
  },
];

/**
 * The variant of `option` for whether its sub-option is checked.
 */
export const getCableOptionVariant = (option: CableOption, subOptionChecked: boolean): CableOptionVariant =>
  option.variants[subOptionChecked ? "checked" : "unchecked"];

/**
 * A short summary of the cable preference in `configuration`.
 */
export const summarizeDataPrepConfiguration = (configuration: DataPrepConfiguration): string => {
  const option = CABLE_OPTIONS.find(({ preference }) => preference === configuration.CablePreference) ?? CABLE_OPTIONS[0];
  return getCableOptionVariant(option, configuration[option.subOption.key]).summary;
};
